import React from "react";
import BackButton from "../../components/BackButton";
import { usePageView } from "../../utils/track";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock3, Crosshair, FileJson, FileSpreadsheet, Gauge, Target } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Badge, EmptyState, InfoRow, Loading, SectionCard, statusTone } from "../../components/ui";
import CompetitionSidebar from "../../components/CompetitionSidebar";
import ResultsBlock from "../../components/ResultsBlock";
import { useApi } from "../../api/client";
import { Competition, CompetitionEvent, MatchInfo, ResultRow, StringRecord } from "../../data/types";
import { downloadCsv, downloadJson } from "../../utils/download";
import { usePortalLink } from "../../utils/portalLink";
import { formatScore } from "../../utils/score";
import { formatVelocity, formatVelocitySpread, useUnits } from "../../utils/units";

export default function MatchResults() {
  usePageView("match-results");
  const link = usePortalLink();
  const { id, eventId, matchId } = useParams();
  const { units } = useUnits();
  const { data: c, loading: cLoading } = useApi<(Competition & { eventList?: CompetitionEvent[] }) | null>(
    id ? `/competitions/${id}` : null,
    null
  );
  const { data: e } = useApi<CompetitionEvent | null>(eventId ? `/events/${eventId}` : null, null);
  const { data: m, loading: mLoading } = useApi<MatchInfo | null>(matchId ? `/matches/${matchId}` : null, null);
  const { data: matchResults } = useApi<ResultRow[]>(matchId ? `/matches/${matchId}/results` : null, []);
  const { data: strings } = useApi<StringRecord[]>(matchId ? `/matches/${matchId}/strings` : null, []);

  if (cLoading || mLoading) return <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5"><div className="card"><Loading /></div></div>;
  if (!c || !m) {
    return (
      <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5">
        <div className="card">
          <EmptyState title="Match not found" message="It may have been removed, or no shoot has been imported yet." />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5">
      <Breadcrumb
        items={[
          { label: "Home", to: "/" },
          { label: "Events", to: "/competitions" },
          { label: c.name, to: `/competitions/${c.id}` },
          ...(e ? [{ label: `Day ${e.n}: ${e.name}`, to: `/competitions/${c.id}/events/${e.id}` }] : []),
          { label: m.name },
        ]}
      />

      <div className="mt-3 flex flex-col gap-4 lg:flex-row">
        <CompetitionSidebar competition={c} active="events" activeEventId={e?.id} events={c.eventList} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-ink">{m.name} Results</h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted">
                <span className="flex items-center gap-1.5"><Calendar size={15} /> {m.date}</span>
                <span className="flex items-center gap-1.5"><Target size={15} /> {m.distance}</span>
                <span className="flex items-center gap-1.5"><Crosshair size={15} /> {m.shots} Shots • {m.scoring}</span>
                {m.startedAt && <span className="flex items-center gap-1.5"><Clock3 size={15} /> Started: {m.startedAt}</span>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2.5">
              <Badge tone={statusTone(m.status)}>{m.status}</Badge>
              {e && (
                <BackButton to={link(`/competitions/${c.id}/events/${e.id}`)} label="Back to Day" />
              )}
            </div>
          </div>

          {/*
            Velocity across the whole field at this range. Shooters compare their
            own spread against it, so it belongs above the leaderboard rather
            than buried in the reference cards below.
          */}
          {m.velocity && m.velocity.count > 0 && (
            <div className="card mt-3 flex flex-wrap items-center gap-x-8 gap-y-2 px-4 py-2.5">
              <span className="flex items-center gap-2 text-[13px] font-bold text-ink">
                <Gauge size={15} className="text-primary" /> Match Velocity
                <span className="font-medium text-muted">({m.velocity.count} shots)</span>
              </span>
              {[
                ["Avg", formatVelocity(m.velocity.avg, units.velocity)],
                ["SD", formatVelocitySpread(m.velocity.sd, units.velocity)],
                ["ES", formatVelocitySpread(m.velocity.es, units.velocity)],
                ["High", formatVelocity(m.velocity.high, units.velocity)],
                ["Low", formatVelocity(m.velocity.low, units.velocity)],
              ].map(([label, value]) => (
                <span key={label} className="flex items-baseline gap-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</span>
                  <span className="text-[15px] font-extrabold text-ink">{value}</span>
                </span>
              ))}
              <span className="text-xs text-muted">{units.velocity}</span>
            </div>
          )}

          {/* Results get the full width — the info cards live below them. */}
          <div className="mt-3 min-w-0">
            <ResultsBlock
              rows={matchResults}
              showHits
              scoreLabel="Score (Total)"
              exportName={`${c.id}-${m.id}-results`}
              stringLinkPrefix={`STR-${m.id}`}
            />
            <div className="card mt-4 p-4">
              <h3 className="text-[15px] font-bold text-ink">Quick Actions</h3>
              <div className="mt-3 flex flex-wrap gap-3">
                <button
                  onClick={() => downloadJson(`${c.id}-${m.id}-results.json`, { competition: c.name, event: e?.name, match: m.name, results: matchResults })}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-ink transition-colors hover:bg-slate-50"
                >
                  <FileJson size={15} /> Export Match Results (JSON)
                </button>
                <button
                  onClick={() =>
                    downloadCsv(
                      `${c.id}-${m.id}-results.csv`,
                      ["Rank", "Shooter", "SID", "Club", "Class", "Grade", "Score", "Centres", "Hits"],
                      matchResults.map((r) => [r.rank, r.shooter, r.sid, r.club, r.rifleClass, r.grade, formatScore(r.score), r.xCount, r.hits ?? ""])
                    )
                  }
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-ink transition-colors hover:bg-slate-50"
                >
                  <FileSpreadsheet size={15} /> Export Match Results (CSV)
                </button>
              </div>
            </div>
          </div>

          {/* Reference cards — always below the results, never a side column. */}
          <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
            {/*
              The conditions this range was shot under — and only what the
              heading above does not already carry. Name, date, distance, shot
              count, scoring and status were each printed twice a few inches
              apart, which invites a reader to compare the two copies rather
              than to trust either.
            */}
            <SectionCard title="Range Conditions" bodyClassName="grid grid-cols-1 gap-x-10 px-5 py-3 sm:grid-cols-2">
              <div>
                {m.sighters !== undefined && (
                  <InfoRow label="Sighters" value={`${m.sighters}${m.convertibleSighters ? " (convertible)" : ""}`} />
                )}
                <InfoRow label="Time Limit" value={m.timeLimit} />
              </div>
              <div>
                {m.startedAt && <InfoRow label="Started" value={m.startedAt} />}
                {m.completedAt && <InfoRow label="Completed" value={m.completedAt} />}
              </div>
            </SectionCard>

            {/* Every string fired at this range — each links to its target map */}
            <SectionCard title={`Shot Strings (${strings.length})`} bodyClassName="p-3">
              {strings.length ? (
                <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {strings.map((s) => (
                    <li key={s.id}>
                      <Link
                        to={link(`/strings/${s.id}`)}
                        className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-[13px] transition-colors hover:bg-slate-50"
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-semibold text-ink">{s.shooter}</span>
                          <span className="text-xs text-muted">
                            {s.rifleClassTag ?? ""} {s.frame ? `· ${s.frame}` : ""}
                          </span>
                        </span>
                        <span className="shrink-0 font-bold text-primary">
                          {s.totalScore}.{s.vCount}
                          {s.centreLabel ?? "V"}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-2 py-6 text-center text-[13px] text-muted">No strings recorded for this range yet.</p>
              )}
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}
